'use client'

import { PriceAlertBanner } from '@/components/client/product/prices/price-alert-banner'
import { PriceConfigurationSelector } from '@/components/client/product/prices/price-configuration-selector'
import type { PricesSectionProps } from '@/components/client/product/prices/price-section-types'
import { PriceUpgradeHighlights } from '@/components/client/product/prices/price-upgrade-highlights'
import { PricesSectionHeader } from '@/components/client/product/prices/prices-section-header'
import { SellerCard } from '@/components/client/product/prices/seller-card'
import { usePriceSelection } from '@/components/client/product/prices/use-price-selection'
import { formatPrice } from '@/lib/utils/format-price'

/**
 * The CONTROL arm of the `product-pricing-ladder` experiment: segmented
 * model/tier selectors, upgrade-highlight chips, and a stacked card per seller.
 * Its counterpart is `prices-ladder-section-client.tsx`; the server assembler
 * picks between them by A/B segment. Keep this arm's behavior frozen while the
 * test runs — a change here changes the baseline.
 */
export const PricesSectionClient = ({
  releaseFullName,
  releaseSlug,
  models,
  defaultModelId,
  tooltips,
  couponText,
}: PricesSectionProps) => {
  const {
    initialModel,
    selectedModel,
    selectedLevelId,
    selectedLevel,
    pricesVaryByModel,
    selectModel,
    selectLevel,
    selectVariant,
  } = usePriceSelection({ models, defaultModelId })
  const levels = selectedModel.levels

  const currentData = selectedModel.levelData[selectedLevelId]
  const sellers = currentData?.sellers ?? []
  const upgradeComparisons = currentData?.upgradeComparisons ?? []
  const isSingleSeller = sellers.length === 1
  // The segmented control shows formatted prices; the payload carries numbers.
  const selectorModels = models.map((model) => ({
    ...model,
    levels: model.levels.map((level) => ({
      ...level,
      lowestPrice: level.lowestPrice != null ? formatPrice(level.lowestPrice) : null,
    })),
  }))
  // Price alerts are stored by technology-level id, not by product/model. Keep
  // the alert named and imaged at family scope, and pass only the tier the user
  // is viewing so the threshold cannot silently come from the first tab.
  const priceAlertRelease = {
    name: releaseFullName,
    imageUrl: initialModel.imageUrl ?? '',
    levels: selectedLevel
      ? [
          {
            id: selectedLevel.id,
            name: selectedLevel.name,
            price: selectedLevel.lowestPrice != null ? formatPrice(selectedLevel.lowestPrice) : '',
          },
        ]
      : [],
  }

  if (sellers.length === 0) {
    return null
  }

  return (
    <div>
      <PricesSectionHeader
        hasSelectors={pricesVaryByModel || levels.length > 1}
        sellerCount={sellers.length}
        pricedName={pricesVaryByModel ? selectedModel.fullName : releaseFullName}
      />

      {/* Only real model × tier combinations reach this selector. Model choice
          is shown only when it changes the available tiers or their prices. */}
      <PriceConfigurationSelector
        models={selectorModels}
        selectedModelId={selectedModel.id}
        selectedLevelId={selectedLevelId}
        showModelSelector={pricesVaryByModel}
        onModelChange={selectModel}
        onLevelChange={selectLevel}
        onVariantChange={selectVariant}
      />

      {/* The key resets disclosure when the selected hearing aid changes. */}
      {selectedLevel && (
        <PriceUpgradeHighlights
          key={`${selectedModel.id}:${selectedLevel.id}`}
          modelName={pricesVaryByModel ? selectedModel.name : undefined}
          levelName={selectedLevel.levelName}
          comparisons={upgradeComparisons}
          tooltips={tooltips}
        />
      )}

      {/* Seller Cards */}
      <div className="mt-6 flex flex-col gap-4">
        {sellers.map((seller, index) => (
          <SellerCard
            key={seller.sellerId}
            seller={seller}
            isPrimary={index === 0 && !isSingleSeller}
            releaseSlug={releaseSlug}
            couponText={couponText}
          />
        ))}
      </div>

      <PriceAlertBanner release={priceAlertRelease} variant="control" />
    </div>
  )
}
